<template>
  <div class="common-page-header">
    <!--  返回按钮  -->
    <div class="back-btn" @click="handleBack">
      <i class="iconfont icon-iconfontzuojiantou"></i>
      <span>返回</span>
    </div>

    <!--  页面标题，如果有tabs传入，则不显示  -->
    <div class="title" v-if="!tabs.length">{{ pageTitle }}</div>

    <!--  页面tabbar  -->
    <div class="header-tabbar" v-if="tabs.length">
      <div
        class="tab-item"
        :class="{ active: tab.id == activeTab }"
        v-for="tab in tabs"
        :key="tab.id"
        @click="handleClickTab(tab)"
      >
        {{ tab.name }}
      </div>
    </div>

    <div class="right-info-box">
      <!-- 已登录：用户头像 + 姓名 + 下拉菜单 -->
      <div class="user-info" v-if="isLogin" v-click-out-side="closeDropMenu">
        <div
          class="user-account"
          :class="{ active: showSettingMenu || showCompanyMenu }"
          @click="toggleDropMenu"
        >
          <div
            v-if="showTextAvatar"
            class="avatar"
            :style="{ backgroundColor: avatarBgColor }"
          >
            {{ avatarText }}
          </div>
          <img v-else class="img-avatar" :src="headUrl" alt="" />
          <div class="name">{{ name }}</div>
          <div class="triangle"></div>
        </div>
        <setting-menu
          :show.sync="showSettingMenu"
          :user-name="name"
          @swicth-company="openCompanyList"
          @change-my-name="afterChangeName"
        ></setting-menu>
        <switch-company-menu :show.sync="showCompanyMenu"></switch-company-menu>
      </div>
      <!-- 未登录：登录/注册入口（权益详情页等允许免登录访问，不显示空壳头像） -->
      <div v-else class="login-entry" @click="goLogin">登录/注册</div>
    </div>
  </div>
</template>

<script>
import SettingMenu from "@/components/PageHeader/SettingMenu";
import SwitchCompanyMenu from "@/components/PageHeader/SwitchCompanyMenu";
import { todealName } from "@/utils";

export default {
  name: "PageHeader",
  components: { SettingMenu, SwitchCompanyMenu },
  props: {
    pageTitle: {
      type: String,
      default: "页面标题",
    },
    tabs: {
      type: Array,
      default: () => [],
    },
    tabId: {
      type: [Number, String],
      default: 0,
    },
    customBack: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTab: this.tabId,
      showSettingMenu: false, // 设置菜单显示or隐藏
      showCompanyMenu: false, // 设置公司切换菜单显示or隐藏
      companyMenuTimer: null, // 打开企业列表的延时器

      // 是否已登录（取不到企业信息即视为未登录，此时不渲染用户区，改显示登录入口）
      // 权益详情等页面允许免登录访问（见 src/permission.js 约定），未登录时不应出现空壳头像
      isLogin: true,
      name: "",
      headUrl: "", // 图片头像的地址
      showTextAvatar: true, // 显示文本头像
      avatarText: "", // 文本头像的文本
      avatarBgColor: "#2aabd2", // 文本头像的背景色
    };
  },
  beforeDestroy() {
    clearTimeout(this.companyMenuTimer);
    window.removeEventListener("pageshow", this.onPageShow);
  },
  created() {
    this.initUserInfo();
    // 从 bfcache 恢复时刷新用户信息（见 onPageShow）
    window.addEventListener("pageshow", this.onPageShow);
  },
  watch: {
    tabId(val) {
      this.activeTab = val;
    },
  },
  methods: {
    // 切换下拉菜单的显示隐藏
    toggleDropMenu() {
      clearTimeout(this.companyMenuTimer);
      if (!this.showSettingMenu && !this.showCompanyMenu) {
        this.showSettingMenu = true;
      } else if (this.showSettingMenu) {
        this.showSettingMenu = false;
      } else if (this.showCompanyMenu) {
        this.showCompanyMenu = false;
      }
    },

    // 打开企业列表
    openCompanyList() {
      this.showSettingMenu = false;
      // 等待设置菜单收起动画（.3s，见 .collapse-transition）结束后再展示企业列表，
      // 否则两个下拉框同位重叠，切换动画看起来很怪
      clearTimeout(this.companyMenuTimer);
      this.companyMenuTimer = setTimeout(() => {
        this.showCompanyMenu = true;
      }, 300);
    },
    // 返回
    handleBack() {
      if (this.customBack) {
        // 如果自定义返回，监听返回事件
        this.$emit("listen-back");
        return;
      } else {
        window.history.go(-1);
      }
    },
    // 切换tab
    handleClickTab(tab) {
      this.activeTab = tab.id;
      this.$emit("changeTab", tab);
    },
    closeDropMenu() {
      clearTimeout(this.companyMenuTimer);
      this.showSettingMenu = false;
      this.showCompanyMenu = false;
    },
    // 未登录时「登录/注册」入口：跳老端登录页（同域，见 SettingMenu 退出登录同款写法）
    goLogin() {
      window.location.href = window.location.origin + "/login.html";
    },
    // 修改名字后
    afterChangeName() {
      this.refreshUserInfo();
    },
    // 从 bfcache 恢复时刷新页头与下拉框的姓名
    //
    // 从专业版订单 / 老端返回时，浏览器可能直接把本页从 bfcache 恢复：Vue 实例与
    // store 缓存全部被冻结、不会重新请求，于是头部和下拉框仍显示改名前的旧值
    // （禅道 #23423 的补充场景）。pageshow 在 bfcache 恢复时带 persisted=true 触发，
    // 正常加载时为 false，这里只处理前者。
    onPageShow(e) {
      if (!e.persisted) return;
      this.refreshUserInfo();
    },
    // 清空企业信息缓存并重新拉取（改名成功后 / bfcache 恢复时）
    refreshUserInfo() {
      // 清空之前的企业信息，否则 getCurCompanyInfo 会直接命中缓存返回旧值
      this.$store.commit("setCurCompanyInfo", null);
      // 重新初始化用户信息
      this.initUserInfo();
    },
    // 设置名称和头像
    async initUserInfo() {
      // 优先使用地址栏上的企业（从老端跳转/切换企业时会带上），避免缓存 COMPANYINFO 过期导致信息取不到
      const routeCompanyId =
        (this.$route && this.$route.query && this.$route.query.companyId) || "";
      const companyInfo = await this.$store.dispatch(
        "getCurCompanyInfo",
        routeCompanyId
      );
      if (companyInfo == null) {
        // 未登录 / 取不到企业信息：标记为未登录，头部改显示「登录/注册」入口
        this.isLogin = false;
        return;
      }
      this.isLogin = true;
      const infoObj = companyInfo;
      // 更新公司信息缓存
      // setStorage("COMPANYINFO", res.obj.userCompany);
      if (infoObj.userVO.headUrl) {
        this.showTextAvatar = false;
        this.headUrl = infoObj.userVO.headUrl;
        this.name = infoObj.userCompany.companyUserName;
      } else if (
        infoObj.userCompany.ownFlag &&
        infoObj.userCompany.companyUserName != "超级管理员"
      ) {
        this.showTextAvatar = true;
        this.avatarText = todealName(infoObj.userCompany.companyUserName)[1];
        this.avatarBgColor = "#ff5800";
        this.name = infoObj.userCompany.companyUserName;
      } else if (
        infoObj.userCompany.ownFlag &&
        infoObj.userCompany.companyUserName == "超级管理员"
      ) {
        this.showTextAvatar = false;
        this.headUrl = require("@/assets/images/header/super.png");
        this.name = "超级管理员";
      } else {
        this.showTextAvatar = true;
        this.avatarText = todealName(infoObj.userCompany.companyUserName)[1];
        this.avatarBgColor = todealName(infoObj.userCompany.companyUserName)[0];
        this.name = infoObj.userCompany.companyUserName;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.common-page-header {
  position: relative;
  width: 100%;
  height: 80px;
  box-sizing: border-box;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #666;
  background-color: #fff;
  box-shadow: 0 6px 5px rgba(0, 0, 0, 0.06);
  z-index: 100;
  .back-btn {
    height: 100%;
    line-height: 80px;
    font-size: 14px;
    color: #333333;
    cursor: pointer;
    i {
      color: #333333;
      font-size: 16px;
      margin-right: 6px;
    }
  }
  .title {
    position: absolute;
    // 居中定位：左右各让出 120px 安全区（宽度 220 固定在窄屏会压到右侧用户区，
    // 实测 375 宽时与用户区重叠 31px）。改用 max-width 让标题随可用空间收缩，
    // 并保留 ellipsis，避免长标题在移动端溢出。
    width: 220px;
    max-width: calc(100% - 240px);
    height: 30px;
    top: 25px;
    left: 50%;
    transform: translateX(-50%);
    color: #333;
    font-size: 22px;
    line-height: 30px;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .header-tabbar {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    .tab-item {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
      padding: 0 12px;
      font-size: 18px;
      margin: 0 10px;
      &:after {
        display: none;
        position: absolute;
        left: 0;
        bottom: 0;
        content: "";
        width: 100%;
        height: 2px;
        background: $--color-primary;
      }
      &.active {
        color: $--color-primary;
        &:after {
          display: block;
        }
      }
    }
  }
  .right-info-box {
    height: 100%;
    font-size: 14px;
    // 未登录：登录/注册入口
    .login-entry {
      height: 100%;
      display: flex;
      align-items: center;
      cursor: pointer;
      color: #0fa87b;
      font-size: 14px;
      &:hover {
        opacity: 0.85;
      }
    }
    .user-info {
      height: 100%;
      .user-account {
        position: relative;
        height: 100%;
        max-width: 120px;
        display: flex;
        align-items: center;
        cursor: pointer;
        .avatar {
          width: 30px;
          min-width: 30px;
          height: 30px;
          background: #2aabd2;
          color: #fff;
          text-align: center;
          line-height: 30px;
          border-radius: 50%;
          font-size: 12px !important;
          margin-right: 10px;
        }
        .img-avatar {
          width: 30px;
          height: 30px;
          margin-right: 10px;
          border-radius: 50%;
        }
        .name {
          flex-shrink: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 70px;
          color: #666666;
          transition: all 0.5s;
        }
        .triangle {
          margin-top: 6px;
          display: inline-block;
          border: 6px solid transparent;
          border-top-color: #bcbcbc;
          -webkit-transform-origin: center 3px;
          -ms-transform-origin: center 3px;
          transform-origin: center 3px;
          -webkit-transition: all 0.5s;
          transition: all 0.5s;
          width: 0;
          margin-left: 8px;
        }
        &.active {
          .name {
            color: $--color-primary;
          }
          .triangle {
            border-top-color: $--color-primary;
            transform: rotate(180deg);
          }
        }
        &:hover {
          .name {
            color: $--color-primary;
          }
          .triangle {
            border-top-color: $--color-primary;
          }
        }
      }
    }
  }
}
// 移动端（H5）：头部整体缩小一档，避免标题与右侧用户区挤压
@media (max-width: 767px) {
  .common-page-header {
    padding: 0 12px;
    .back-btn {
      font-size: 13px;
    }
    .title {
      max-width: calc(100% - 200px);
      font-size: 17px;
    }
    .user-info .user-account .name {
      max-width: 56px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
